/**
 * Dnd5eSheetAdapter — maps CharacterSheet → HudCharacterData.
 * Read-only direction. NEVER throws exceptions (ADR §8.12).
 * Returns coerced data + warnings array.
 *
 * T002 NOTE: Return type is `RulesCharacterData` — the structural supertype
 * defined in ../rules/index.ts. Frontend `HudCharacterData` is directly
 * assignable to (and from, when shapes match) this type without
 * `as unknown as` casts. The adapter implementation is unchanged; type
 * unification happens in ../rules/index.ts and src/lib/character-hud-store.ts.
 *
 * Why we don't return `HudCharacterData` directly: shared/ must not depend
 * on src/. `RulesCharacterData` is the canonical shared bridge type.
 */

import type { CharacterSheet, SheetTemplate, Ability, ActiveEffect } from '../../../domain.js';
import type { SpellbookEntry, DeathSaveState, DeathSaveStatus, HitDiceData, ClassEntry } from '../types.js';
import type { SrdSpell, SrdEquipmentItem, SrdClass } from '../srd/types.js';
import { dnd5eRules } from '../rules/index.js';
import type {
  RulesCharacterData,
  RulesFeature,
  AbilityStat,
  SkillId,
} from '../rules/index.js';
import { SKILL_TO_STAT } from '../rules/index.js';
import { SKILL_KEY_MAP } from '../skill-keys.js';
import { normalizeConditionName, parseConditionDurations } from '../conditions.js';
import { computeAcBreakdown } from '../EquipmentAutomation.js';
import type { ClassFeatures } from '../EquipmentAutomation.js';

// ─── Types ───────────────────────────────────────────────────────

export type SheetReadRole = 'gm' | 'owner' | 'party-basic' | 'party-full';

export interface AdapterResult {
  data: RulesCharacterData;
  warnings: string[];
}

/**
 * Senses shape (B7). RulesCharacterData itself does not yet declare this
 * optional field, so we widen the local data variable via intersection.
 * Frontend HudCharacterData declares the corresponding optional `senses?`
 * — see src/lib/character-hud-store.ts.
 * Contract for darkvision (number, ft) follows shared/domain.ts:727-730
 * (TokenVision.darkvision).
 */
type HudSenses = {
  darkvision?: number;
  blindsight?: number;
  tremorsense?: number;
  truesight?: number;
};

// Local alias — HudResource lives in src/lib/character-hud-store.ts and
// the shared/→src/ boundary (see file header) forbids importing it here.
// Shape matches the canonical HudResource contract.
type HudResource = { id: string; name: string; current: number; max: number; recharge: 'short' | 'long' | 'dawn' };

// Local-widened slot shape: carries an optional `pact` flag so the HUD
// can render warlock pact-magic slots distinctly from regular caster
// slots. Structurally assignable to the narrower
// RulesCharacterData['spells']['slots'] value type.
type HudSpellSlot = { max: number; current: number; pact?: boolean };

// T003: locally-widened death-save shape carrying the canonical `status`
// (alive/dying/stable/dead) alongside the success/failure tallies. The
// narrower RulesCharacterData['deathSaves'] value type ({ successes,
// failures }) tolerates the extra property under structural typing, and
// the frontend HudCharacterData['deathSaves'] declares the matching
// optional `status` — see src/lib/character-hud-store.ts.
type HudDeathSaves = RulesCharacterData['deathSaves'] & { status: DeathSaveStatus };

// AC breakdown for the Defenses panel. Itemised contributions that sum to the
// authoritative `ac`. `effectsBonus` is the residual between the stored AC
// (which already includes active-effect bonuses, e.g. the Shield spell) and the
// inventory+misc-derived total, so the displayed rows always add up to `ac`.
// Lives on HudCharacterData (src/); RulesCharacterData ignores the extra prop.
type HudAcBreakdown = {
  base: number;
  armorName: string | null;
  armorBonus: number;
  dexMod: number;
  shieldBonus: number;
  unarmoredBonus: number;
  miscBonus: number;
  effectsBonus: number;
  total: number;
};

// ─── Skill mapping table ─────────────────────────────────────────


// ─── Coercion helpers ────────────────────────────────────────────

function coerceNumber(val: unknown, fallback: number): number {
  if (typeof val === 'number' && !isNaN(val)) return val;
  if (typeof val === 'string') {
    const n = Number(val);
    if (!isNaN(n)) return n;
  }
  return fallback;
}

function coerceString(val: unknown, fallback = ''): string {
  if (typeof val === 'string') return val;
  return String(val ?? fallback);
}

function coerceBoolean(val: unknown): boolean {
  return val === true || val === 'true' || val === 1 || val === '1';
}

/**
 * Read a save/skill proficiency tier as a numeric 0 | 1 | 2
 * (none / proficient / expertise). Mirrors `profLevel` in
 * ../sheet-values-schema.ts — the stored value is now numeric, so we
 * forward it raw and clamp to the valid tier range instead of collapsing
 * to a boolean (which previously dropped expertise=2 down to 1). Legacy
 * boolean `true` maps to proficient(1); anything non-numeric clamps to 0.
 */
function coerceProfLevel(val: unknown): 0 | 1 | 2 {
  let n: number;
  if (typeof val === 'number') n = val;
  else if (typeof val === 'string') n = Number(val);
  else if (val === true) n = 1;
  else n = 0;
  if (!isFinite(n) || n <= 0) return 0;
  if (n >= 2) return 2;
  return 1;
}

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

// ─── Default empty data ──────────────────────────────────────────

function emptyStats(): Record<AbilityStat, number> {
  return { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
}

function emptySaves(): Record<AbilityStat, 0 | 1> {
  return { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 };
}

function emptySkills(): Record<SkillId, 0 | 1 | 2> {
  const skills: Record<string, 0 | 1 | 2> = {};
  for (const key of Object.keys(SKILL_TO_STAT)) {
    skills[key] = 0;
  }
  return skills as Record<SkillId, 0 | 1 | 2>;
}

// ─── Adapter ─────────────────────────────────────────────────────

export class Dnd5eSheetAdapter {
  static fromSheet(
    sheet: CharacterSheet,
    _template: SheetTemplate,
    role: SheetReadRole,
    srdSpells?: Map<string, SrdSpell>,
    srdEquipment?: Map<string, SrdEquipmentItem>,
    _srdClasses?: Map<string, SrdClass>,
  ): AdapterResult {
    const warnings: string[] = [];

    try {
      const v = sheet.values;

      // Guard: party-basic → minimal mapping
      if (role === 'party-basic') {
        return Dnd5eSheetAdapter.minimalMapping(sheet, warnings);
      }

      // ── Identity ──
      // 2026-06-02: subrace/subclass/gender/height/weight/age reads added —
      // WRITE_KEY_MAP уже маппит эти paths (write-adapter.ts:92-97), но adapter
      // не читал их обратно → IdentitySection edits round-tripped server-side
      // но UI всегда показывал undefined / placeholder.
      const level = clamp(coerceNumber(v.level, 1), 1, 20);
      const identity = {
        name: sheet.name || 'Безымянный',
        race: coerceString(v.race),
        subrace: typeof v.subrace === 'string' ? v.subrace : undefined,
        className: coerceString(v.class_name),
        subclass: typeof v.subclass === 'string' ? v.subclass : undefined,
        level,
        alignment: coerceString(v.alignment),
        background: typeof v.background === 'string' ? v.background : undefined,
        gender: typeof v.gender === 'string' ? v.gender : undefined,
        height: typeof v.height === 'string' ? v.height : undefined,
        weight: typeof v.weight === 'string' ? v.weight : undefined,
        age: typeof v.age === 'number' ? v.age : undefined,
        // Roleplay "Who I Am" (HeroCard) freeform flavor — read back from values.*
        // (write-adapter routes identity.physicalDescription/appearance/quote here)
        // so optimistic edits confirm instead of tripping the write-timeout.
        physicalDescription: typeof v.physical_description === 'string' ? v.physical_description : undefined,
        appearance: typeof v.appearance === 'string' ? v.appearance : undefined,
        quote: typeof v.quote === 'string' ? v.quote : undefined,
        portrait: sheet.portrait ?? undefined,
      };

      // ── Stats ──
      const stats = emptyStats();
      const STAT_KEYS: AbilityStat[] = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
      for (const stat of STAT_KEYS) {
        const raw = v[stat];
        const val = coerceNumber(raw, 10);
        if (raw !== undefined && typeof raw !== 'number') {
          warnings.push(`stats.${stat}: coerced "${raw}" to ${val}`);
        }
        stats[stat] = val;
      }

      // ── Saves ──
      // Stored as a profLevel tier (0|1|2) per sheet-values-schema.ts. The
      // HUD save slot only renders proficient/not (RulesCharacterData.saves
      // is 0|1), so expertise(2) — not a real save concept in 5e — collapses
      // to proficient. Skills keep the full tier below.
      const saves = emptySaves();
      for (const stat of STAT_KEYS) {
        saves[stat] = coerceProfLevel(v[`${stat}_save_prof`]) >= 1 ? 1 : 0;
      }

      // ── Skills ──
      // Forward the raw numeric tier (0|1|2) so expertise(2) survives the
      // read instead of being flattened by a boolean coercion.
      const skills = emptySkills();
      for (const [skillId, sheetKey] of Object.entries(SKILL_KEY_MAP) as [SkillId, string][]) {
        skills[skillId] = coerceProfLevel(v[sheetKey]);
      }

      // ── HP ──
      const hp = {
        current: coerceNumber(v.hp, 0),
        max: coerceNumber(v.max_hp, 0),
        temp: coerceNumber(v.temp_hp, 0),
      };
      if (typeof v.hp === 'string') {
        warnings.push(`hp: coerced string "${v.hp}" to ${hp.current}`);
      }

      // ── AC ──
      // `ac` is authoritative: sheet.values.ac is maintained server-side
      // (recomputeDnd5eAc on equip/stat/misc edits) and already includes any
      // active-effect bonuses. When the SRD equipment table is available we also
      // itemise the contributions for the Defenses breakdown, using the SAME
      // computeAcBreakdown the server uses → the rows reconcile with `ac`. Any
      // residual (effects / legacy manual AC) is surfaced as `effectsBonus` so
      // the displayed pieces always sum to `ac`.
      const ac = coerceNumber(v.ac, 10);
      const miscAc = coerceNumber(v.misc_ac, 0);
      const armorAc = coerceNumber(v.armor_ac, 0);
      const shieldOn = v.shield_on === true;
      let acBreakdown: HudAcBreakdown | undefined;
      if (srdEquipment) {
        try {
          const classFeatures: ClassFeatures = {};
          for (const cd of sheet.dnd5e?.classData ?? []) {
            if (cd.classSlug === 'barbarian') { classFeatures.unarmoredDefense = 'barbarian'; break; }
            if (cd.classSlug === 'monk') { classFeatures.unarmoredDefense = 'monk'; break; }
          }
          const bd = computeAcBreakdown(
            sheet.repeatingData['inventory'] ?? [],
            srdEquipment,
            dnd5eRules.abilityMod(stats.dex), miscAc, classFeatures,
            dnd5eRules.abilityMod(stats.con), dnd5eRules.abilityMod(stats.wis),
            { armorBonus: armorAc, shieldOn },
          );
          acBreakdown = {
            base: bd.base, armorName: bd.armorName, armorBonus: bd.armorBonus, dexMod: bd.appliedDex,
            shieldBonus: bd.shieldBonus, unarmoredBonus: bd.unarmoredBonus,
            miscBonus: bd.miscBonus, effectsBonus: ac - bd.total, total: ac,
          };
        } catch {
          warnings.push('computeAcBreakdown failed, breakdown omitted');
        }
      }

      // ── Speed ──
      const speed = coerceNumber(v.speed, 30);
      const speedClimb = Math.max(0, coerceNumber(v.speed_climb, 0));
      const speedSwim = Math.max(0, coerceNumber(v.speed_swim, 0));
      const speedFly = Math.max(0, coerceNumber(v.speed_fly, 0));

      // ── Senses ──
      // Read movement-independent senses (ft) from sheet values. Emit the senses
      // object when ANY is non-zero so the Defenses panel can render + edit them.
      const darkvision = Math.max(0, coerceNumber(v.darkvision, 0));
      const blindsight = Math.max(0, coerceNumber(v.blindsight, 0));
      const tremorsense = Math.max(0, coerceNumber(v.tremorsense, 0));
      const truesight = Math.max(0, coerceNumber(v.truesight, 0));
      const senses: HudSenses | undefined =
        (darkvision || blindsight || tremorsense || truesight)
          ? { darkvision, blindsight, tremorsense, truesight }
          : undefined;

      // ── Damage modifiers (resistances / immunities / vulnerabilities) ──
      // Free lists of damage-type labels, stored as string[] on the sheet.
      // Always emitted (even empty) so the Defenses editor round-trips cleanly
      // via the generic sheet:update path. Mirrors the `conditions` convention.
      const toStringArray = (raw: unknown): string[] =>
        Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
      const resistances = toStringArray(v.damage_resistances);
      const immunities = toStringArray(v.damage_immunities);
      const vulnerabilities = toStringArray(v.damage_vulnerabilities);

      // ── Combat notes (F-005/BF-008 round-trip) ──
      const combat_notes = typeof v.combat_notes === 'string' ? v.combat_notes : undefined;

      // ── Combat ──
      const attacks = mapAttacks(sheet, warnings);
      // 2026-06-01: initiative и actionEconomy теперь honor server-stored values.
      // initiative previously hardcoded к abilityMod(dex) → user edits снапались
      // обратно. Server-side template formula 'dex_mod' applies в recalcComputedFields,
      // но пользовательский override через sheet:update теперь visible до recalc.
      // actionEconomy stored как JSON string в values.action_economy; parse here
      // чтобы UI tile spent/available reflected actual state.
      // Wave 6 (2026-06-02): initiative_bonus is user-editable hybrid bonus.
      // Template formula `initiative = dex_mod + initiative_bonus` recalcs the
      // stored `initiative` value; we surface the bonus piece separately so UI
      // can render the breakdown and let user edit it via Stepper.
      const initiativeBonus = coerceNumber(v.initiative_bonus, 0);
      const combat = {
        initiative: coerceNumber(v.initiative, dnd5eRules.abilityMod(stats.dex) + initiativeBonus),
        initiativeBonus,
        round: coerceNumber(v.round, 0),
        attacks,
        actionEconomy: parseActionEconomy(v),
        // Persisted turn-movement; the server's ActionEconomyResetSubscriber
        // zeroes values.movement_spent on initiative:next alongside
        // action_economy. Previously not read → CombatTab held it in local
        // state and it silently reset on remount/reload.
        movementSpent: coerceNumber(v.movement_spent, 0),
        notes: combat_notes,
      };

      // Wave 6 — passive_perception_bonus similarly surfaces the editable bonus.
      const passivePerceptionBonus = coerceNumber(v.passive_perception_bonus, 0);

      // ── Spells ──
      const spellcastingAbility = coerceString(v.spellcasting_ability, 'wis') as AbilityStat;
      const validStats: AbilityStat[] = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
      const spellStat = validStats.includes(spellcastingAbility) ? spellcastingAbility : 'wis';
      const spells = mapSpells(sheet, spellStat, srdSpells, warnings);

      // ── Death Saves ──
      const deathSaves = mapDeathSaves(sheet, warnings);

      // ── Features ──
      const features = mapFeatures(sheet, warnings);

      // ── Inventory ──
      const inventory = mapInventory(sheet, srdEquipment, warnings);

      // ── Currency ──
      const currency = {
        cp: Math.max(0, coerceNumber(v.cp, 0)),
        sp: Math.max(0, coerceNumber(v.sp, 0)),
        ep: Math.max(0, coerceNumber(v.ep, 0)),
        gp: Math.max(0, coerceNumber(v.gp, 0)),
        pp: Math.max(0, coerceNumber(v.pp, 0)),
      };

      // ── Class Data ──
      const classData = mapClassData(sheet, warnings);

      // ── Hit Dice ──
      const hitDice = mapHitDice(sheet, warnings);

      // ── Active Effects ──
      const activeEffects = mapActiveEffects(sheet);

      // ── Proficiencies ──
      const proficiencies = mapProficiencies(sheet);
      // Parallel category list ("namecategory") so the user's chosen
      // proficiency category survives instead of being re-inferred by keyword.
      const proficiencyCats = Array.isArray(v.proficiency_cats)
        ? (v.proficiency_cats as unknown[]).filter((x): x is string => typeof x === 'string')
        : [];

      // ── Resources ──
      const resources = mapResources(sheet);

      // ── Roleplay ──
      const rp = mapRoleplay(sheet);

      // ── Inspiration ──
      const inspiration = coerceBoolean(v.inspiration);

      // ── XP ──
      const xp = v.xp !== undefined ? coerceNumber(v.xp, undefined as unknown as number) : undefined;

      // Local widening: senses lives on HudCharacterData (src/) but not on
      // RulesCharacterData (shared/). Carrying it on the runtime object is
      // safe — the structural supertype simply ignores the extra optional
      // property when downstream code reads through RulesCharacterData.
      const data: RulesCharacterData & { senses?: HudSenses; acBreakdown?: HudAcBreakdown; miscAc?: number; armorAc?: number; shieldOn?: boolean; proficiencyCats?: string[]; speedClimb?: number; speedSwim?: number; speedFly?: number; resistances?: string[]; immunities?: string[]; vulnerabilities?: string[] } = {
        identity,
        stats,
        saves,
        skills,
        hp,
        ac,
        miscAc,
        armorAc,
        shieldOn,
        speed,
        speedClimb,
        speedSwim,
        speedFly,
        combat,
        spells,
        deathSaves,
        features,
        inventory,
        currency,
        classData,
        hitDice,
        activeEffects,
        proficiencies,
        proficiencyCats,
        resources,
        rp,
        inspiration,
        xp: typeof xp === 'number' && !isNaN(xp) ? xp : undefined,
        combat_notes,
        passivePerceptionBonus,
        resistances,
        immunities,
        vulnerabilities,
      };
      if (senses !== undefined) {
        data.senses = senses;
      }
      if (acBreakdown !== undefined) {
        data.acBreakdown = acBreakdown;
      }

      return { data, warnings };
    } catch (err) {
      warnings.push(`Adapter error: ${err instanceof Error ? err.message : String(err)}`);
      return { data: emptyCharacterData(), warnings };
    }
  }

  /** Minimal mapping for party-basic role — only publicFields + name + basic combat stats */
  private static minimalMapping(sheet: CharacterSheet, warnings: string[]): AdapterResult {
    const v = sheet.values;
    const publicFields = new Set(sheet.publicFields);
    const data: RulesCharacterData = {
      identity: {
        name: sheet.name || 'Безымянный',
        level: publicFields.has('level') ? clamp(coerceNumber(v.level, 1), 1, 20) : 1,
        race: publicFields.has('race') ? coerceString(v.race) : '',
        className: publicFields.has('class_name') ? coerceString(v.class_name) : '',
        portrait: sheet.portrait ?? undefined,
      },
      stats: emptyStats(),
      saves: emptySaves(),
      skills: emptySkills(),
      hp: {
        current: publicFields.has('hp') ? coerceNumber(v.hp, 0) : 0,
        max: publicFields.has('max_hp') ? coerceNumber(v.max_hp, 0) : 0,
        temp: 0,
      },
      ac: publicFields.has('ac') ? coerceNumber(v.ac, 10) : 10,
      speed: 30,
      combat: {
        initiative: 0, round: 0, attacks: [],
        actionEconomy: { action: true, bonus: true, reaction: true },
      },
      spells: { stat: 'wis', slots: {}, known: [], concentrationSpellId: null },
      deathSaves: { successes: 0, failures: 0 },
      features: [],
      inventory: [],
      currency: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },
      classData: [],
      hitDice: { total: 0, used: 0, dieType: 'd8' },
      activeEffects: [],
      proficiencies: [],
      resources: Array.isArray(v.resources) ? v.resources as unknown as HudResource[] : [],
      rp: emptyRoleplay(),
      inspiration: false,
    };
    return { data, warnings };
  }
}

// ─── Mapping helpers ─────────────────────────────────────────────

function mapAttacks(sheet: CharacterSheet, _warnings: string[]): RulesCharacterData['combat']['attacks'] {
  const rows = sheet.repeatingData?.attacks ?? [];
  return rows.map((r, i) => ({
    id: r.rowId ?? `attack-${i}`,
    name: coerceString(r.values.name, 'Атака'),
    attackBonus: coerceNumber(r.values.to_hit, 0),
    damage: coerceString(r.values.damage_dice, '1d4'),
    damageType: coerceString(r.values.damage_type, ''),
    meta: coerceString(r.values.properties),
  }));
}

/** Parse an SRD components string ("V, S, M (a pinch of soot)") into the HUD
 * component flags + material description. Mirrors the client-side parser. */
function parseSrdComponentsForHud(str: string): { v?: boolean; s?: boolean; m?: boolean; materialDesc?: string } {
  const materialMatch = /M\s*\(([^)]*)\)/.exec(str);
  return {
    v: /\bV\b/.test(str),
    s: /\bS\b/.test(str),
    m: /\bM\b/.test(str),
    materialDesc: materialMatch ? materialMatch[1].trim() : undefined,
  };
}

function mapSpells(
  sheet: CharacterSheet,
  spellStat: AbilityStat,
  srdSpells: Map<string, SrdSpell> | undefined,
  warnings: string[],
): RulesCharacterData['spells'] {
  const dnd5e = sheet.dnd5e;

  // Spell slots — locally widened to carry an optional `pact` flag.
  // Warlock pact-magic slots are stored separately on the source sheet
  // (sheet.dnd5e.spellSlots.pact) and must surface to the HUD with a
  // distinct visual treatment. We project the pact slot into the same
  // level-keyed map with `pact: true`; the narrower
  // RulesCharacterData['spells']['slots'] value type tolerates the extra
  // optional property under structural typing.
  const slots: Record<number, HudSpellSlot> = {};
  if (dnd5e?.spellSlots?.slots) {
    for (const [lvlStr, slot] of Object.entries(dnd5e.spellSlots.slots)) {
      const lvl = Number(lvlStr);
      const max = Math.max(0, coerceNumber(slot.max, 0));
      const current = Math.max(0, coerceNumber(slot.current, 0));
      if (current < 0) {
        warnings.push(`spellSlots[${lvl}].current: clamped ${current} to 0`);
      }
      slots[lvl] = { max, current: Math.min(current, max), pact: false };
    }
  }
  if (dnd5e?.spellSlots?.pact) {
    const pact = dnd5e.spellSlots.pact;
    const lvl = clamp(coerceNumber(pact.level, 1), 1, 9);
    const max = Math.max(0, coerceNumber(pact.max, 0));
    const current = Math.max(0, coerceNumber(pact.current, 0));
    slots[lvl] = { max, current: Math.min(current, max), pact: true };
  }

  // Spellbook
  const known: RulesCharacterData['spells']['known'] = [];
  const spellbook: SpellbookEntry[] = dnd5e?.spellbook ?? [];
  for (const entry of spellbook) {
    const spell: RulesCharacterData['spells']['known'][0] = {
      id: entry.spellSlug || entry.name,
      spellSlug: entry.spellSlug || undefined,
      name: entry.name,
      level: entry.level,
      state: entry.state,
      school: entry.school,
      components: entry.components,
      ritual: entry.ritual,
      concentration: entry.concentration,
      description: entry.description,
      higherLevels: entry.higherLevels,
      actionType: entry.actionType,
    };

    // SRD enrichment — fill any field the stored entry lacks (legacy entries
    // persisted before #spells P1 carry only name/level/state) from the SRD
    // catalog by slug, so the HUD shows school/components/ritual/concentration.
    if (srdSpells) {
      const srd = srdSpells.get(entry.spellSlug);
      if (srd) {
        if (spell.school === undefined && srd.school) spell.school = srd.school;
        if (spell.ritual === undefined) spell.ritual = srd.ritual;
        if (spell.concentration === undefined) spell.concentration = srd.concentration;
        if (spell.components === undefined && srd.components) {
          spell.components = parseSrdComponentsForHud(srd.components);
        }
        if (spell.castingTime === undefined && srd.castingTime) spell.castingTime = srd.castingTime;
        if (spell.range === undefined && srd.range) spell.range = srd.range;
        if (spell.duration === undefined && srd.duration) spell.duration = srd.duration;
        if (spell.description === undefined && srd.description) spell.description = srd.description;
        if (spell.higherLevels === undefined && srd.higherLevels) spell.higherLevels = srd.higherLevels;
        // Derive attack/save discriminator from the description so the cast
        // action does a real roll (vs the demo toast). SRD has no structured
        // field, so parse the canonical wording.
        if (spell.roll === undefined && srd.description) {
          const d = srd.description.toLowerCase();
          if (/ranged spell attack/.test(d)) spell.roll = { attack: 'ranged' };
          else if (/melee spell attack/.test(d)) spell.roll = { attack: 'melee' };
          else {
            const m = /\b(strength|dexterity|constitution|intelligence|wisdom|charisma) saving throw/.exec(d);
            if (m) {
              const ab: Record<string, AbilityStat> = { strength: 'str', dexterity: 'dex', constitution: 'con', intelligence: 'int', wisdom: 'wis', charisma: 'cha' };
              spell.roll = { saveStat: ab[m[1]] };
            }
          }
        }
      }
    }

    // Derive the action-economy cost from the castingTime string when the
    // entry carries no explicit override — the field was previously never
    // populated at all, so every spell auto-spent as a full action and the
    // «bonus» column never rendered. Understands EN (SRD) and RU strings.
    if (spell.actionType === undefined && spell.castingTime) {
      spell.actionType = deriveActionType(spell.castingTime);
    }

    known.push(spell);
  }

  return {
    stat: spellStat,
    slots,
    known,
    concentrationSpellId: dnd5e?.concentrationSpellId ?? null,
  };
}

/**
 * T003: Death-save status surfacing + single source of truth.
 * The canonical death-save state lives at `sheet.dnd5e.deathSaveState`
 * (DeathSaveState in shared/systems/dnd5e/types.ts), which now carries the
 * authoritative `status` (alive/dying/stable/dead) alongside the
 * success/failure tallies. We forward that `status` to the HUD so the
 * frontend never has to re-derive liveness from HP — the server's
 * death-save and clear handlers are the single writers of this field.
 * When the canonical state is absent, default to a fully-alive shape.
 */
function mapDeathSaves(sheet: CharacterSheet, _warnings: string[]): HudDeathSaves {
  const ds: DeathSaveState | undefined = sheet.dnd5e?.deathSaveState;
  if (!ds) return { successes: 0, failures: 0, status: 'alive' };
  return {
    successes: clamp(coerceNumber(ds.successes, 0), 0, 3),
    failures: clamp(coerceNumber(ds.failures, 0), 0, 3),
    status: coerceDeathSaveStatus(ds.status),
  };
}

/**
 * Clamp an unknown value to the DeathSaveStatus union. Defaults to 'alive'
 * for any unrecognized payload so a malformed canonical state never leaves
 * the HUD without a renderable status.
 */
function coerceDeathSaveStatus(val: unknown): DeathSaveStatus {
  if (val === 'alive' || val === 'dying' || val === 'stable' || val === 'dead') {
    return val;
  }
  return 'alive';
}

function mapFeatures(sheet: CharacterSheet, _warnings: string[]): RulesFeature[] {
  const abilities: Ability[] = sheet.abilities ?? [];
  return abilities.map(a => {
    const feature: RulesFeature = {
      id: a.id,
      name: a.name,
      source: a.source ?? '',
      description: a.description,
    };
    if (a.maxUses !== null && a.maxUses > 0) {
      feature.uses = {
        current: coerceNumber(a.currentUses, 0),
        max: a.maxUses,
        recharge: a.restType === 'none' ? 'long' : a.restType,
      };
    }
    return feature;
  });
}

function mapInventory(
  sheet: CharacterSheet,
  srdEquipment: Map<string, SrdEquipmentItem> | undefined,
  _warnings: string[],
): RulesCharacterData['inventory'] {
  const rows = sheet.repeatingData?.inventory ?? [];
  return rows.map((r, i) => {
    const rawType = coerceString(r.values.item_type, 'gear');
    const item_type: 'weapon' | 'armor' | 'shield' | 'gear' =
      rawType === 'weapon' || rawType === 'armor' || rawType === 'shield' ? rawType : 'gear';
    const rawCat = coerceString(r.values.armor_category, 'Light');
    const armor_category: 'Light' | 'Medium' | 'Heavy' =
      rawCat === 'Medium' || rawCat === 'Heavy' ? rawCat : 'Light';
    const srd_slug = typeof r.values.srd_slug === 'string' && r.values.srd_slug ? r.values.srd_slug : undefined;
    // SRD enrichment — backfill the item description from the equipment
    // catalog by slug when the stored row carries none. Mirrors the mapSpells
    // backfill: purely additive, never overwrites authored content, and looks
    // the item up by slug in the passed map (so homebrew items whose slug is
    // present in the merged map enrich too).
    let description = typeof r.values.description === 'string' ? r.values.description : undefined;
    if ((description === undefined || description === '') && srd_slug && srdEquipment) {
      const srd = srdEquipment.get(srd_slug);
      if (srd?.description) description = srd.description;
    }
    return {
      id: r.rowId ?? `item-${i}`,
      name: coerceString(r.values.name, 'Предмет'),
      quantity: coerceNumber(r.values.quantity, 1),
      weight: typeof r.values.weight === 'number' ? r.values.weight : undefined,
      equipped: coerceBoolean(r.values.equipped),
      description,
      item_type,
      srd_slug,
      base_ac: coerceNumber(r.values.base_ac, 0),
      armor_category,
      attuned: coerceBoolean(r.values.attuned),
      // Derived: legacy `weapon` boolean follows item_type (single source of truth).
      weapon: item_type === 'weapon',
    };
  });
}

function mapClassData(sheet: CharacterSheet, _warnings: string[]): RulesCharacterData['classData'] {
  const classData: ClassEntry[] = sheet.dnd5e?.classData ?? [];
  return classData.map(c => ({
    className: c.classSlug || 'unknown',
    subclass: c.subclassSlug ?? undefined,
    level: c.level,
    hitDie: `d${getHitDieForClass(c.classSlug)}`,
  }));
}

function mapHitDice(sheet: CharacterSheet, _warnings: string[]): RulesCharacterData['hitDice'] {
  const hd: HitDiceData | null | undefined = sheet.dnd5e?.hitDice;
  if (hd) {
    return {
      total: coerceNumber(hd.total, 0),
      used: coerceNumber(hd.used, 0),
      dieType: `d${hd.dieType}`,
    };
  }

  // Fallback from values. Legacy / builder-seeded sheets that never got a
  // structured dnd5e.hitDice store `hit_dice` as a FORMULA string "NdM"
  // (e.g. "7d8" for a level-7 bard), NOT a bare count. Parse N as the total
  // dice count and dM as the die type; otherwise coerceNumber("7d8") yields
  // NaN→0 and a level-7 character shows "d8×0" (zero hit dice to spend).
  const rawHd = sheet.values.hit_dice;
  const formula = typeof rawHd === 'string' ? rawHd.match(/^\s*(\d+)\s*(d\d+)/i) : null;
  if (formula) {
    return {
      total: Number(formula[1]),
      used: coerceNumber(sheet.values.hit_dice_used, 0),
      dieType: formula[2].toLowerCase(),
    };
  }
  return {
    total: coerceNumber(sheet.values.hit_dice, 0),
    used: coerceNumber(sheet.values.hit_dice_used, 0),
    dieType: 'd8',
  };
}

/**
 * Parse `values.action_economy` JSON string → structured object. Default
 * («все доступно») used когда field absent or malformed. Server's
 * `initiative:next` handler auto-resets к этому defaults на смене turn'а.
 */
function parseActionEconomy(v: CharacterSheet['values']): { action: boolean; bonus: boolean; reaction: boolean } {
  const raw = v.action_economy;
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        return {
          action: obj.action !== false,
          bonus: obj.bonus !== false,
          reaction: obj.reaction !== false,
        };
      }
    } catch {
      // Malformed JSON — fall through к defaults.
    }
  }
  return { action: true, bonus: true, reaction: true };
}

/** castingTime → action-economy bucket. EN (SRD: "1 action", "1 bonus action",
 * "1 reaction", "1 minute", "8 hours") + RU ("действие", "бонусное действие",
 * "реакция", "1 минута", "1 час"). Unrecognized → 'special' (not tracked).
 * Exported: the AddSpellForm pre-fills its action-type select from the SRD
 * pick's castingTime. */
export function deriveActionType(castingTime: string): NonNullable<RulesCharacterData['spells']['known'][0]['actionType']> {
  const s = castingTime.toLowerCase();
  if (/bonus|бонус/.test(s)) return 'bonus';
  if (/reaction|реак/.test(s)) return 'reaction';
  if (/minute|минут/.test(s)) return 'minute';
  if (/hour|час/.test(s)) return 'hour';
  if (/action|действ/.test(s)) return 'action';
  return 'special';
}

function mapActiveEffects(sheet: CharacterSheet): RulesCharacterData['activeEffects'] {
  const effects: ActiveEffect[] = sheet.activeEffects ?? [];
  const fromStructural = effects.map(e => ({
    id: e.id,
    name: `${e.target} ${e.mode} ${e.value}`,
    source: e.source.name ?? '',
    duration: undefined,
    description: undefined,
  }));
  // T205-fix (2026-06-01): bridge sheet.values.conditions → activeEffects so
  // ConditionToggleRow (DefensesSection) can show active chips via case-insensitive
  // name match. Server stores conditions в `sheet.values.conditions` (string[]),
  // но `sheet.activeEffects` структурный — без bridge UI чипы никогда не загораются
  // даже когда server успешно processes sheet:condition-toggle.
  const rawConditions = sheet.values?.conditions;
  const conditionNames: string[] = Array.isArray(rawConditions)
    ? rawConditions.filter((c): c is string => typeof c === 'string')
    : [];
  // Normalize to the canonical key so UI matching is dialect-independent —
  // legacy sheets carry RU labels / lowercase ids in values.conditions.
  // Timed conditions surface their remaining rounds as the pill's duration
  // (values.condition_durations, ticked by ConditionDurationSubscriber).
  const durations = parseConditionDurations(sheet.values?.condition_durations);
  const fromConditions = conditionNames.map(normalizeConditionName).map((name) => ({
    id: `condition:${name}`,
    name,
    source: 'condition',
    duration: durations[name] ? `${durations[name]} р.` : undefined,
    description: undefined,
  }));
  return [...fromStructural, ...fromConditions];
}

function emptyRoleplay(): RulesCharacterData['rp'] {
  return {
    ideal: '',
    bond: '',
    flaw: '',
    backstory: '',
    manners: '',
    goals: [],
    npcs: [],
    factions: [],
    sessionLog: [],
    moments: [],
    boardConfig: { cards: [], autoLayout: false },
  };
}

function emptyCharacterData(): RulesCharacterData {
  return {
    identity: { name: 'Безымянный', level: 1 },
    stats: emptyStats(),
    saves: emptySaves(),
    skills: emptySkills(),
    hp: { current: 0, max: 0, temp: 0 },
    ac: 10,
    speed: 30,
    combat: { initiative: 0, round: 0, attacks: [], actionEconomy: { action: true, bonus: true, reaction: true } },
    spells: { stat: 'wis', slots: {}, known: [], concentrationSpellId: null },
    deathSaves: { successes: 0, failures: 0 },
    features: [],
    inventory: [],
    currency: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },
    classData: [],
    hitDice: { total: 0, used: 0, dieType: 'd8' },
    activeEffects: [],
    proficiencies: [],
    resources: [],
    rp: emptyRoleplay(),
    inspiration: false,
  };
}

function mapProficiencies(sheet: CharacterSheet): string[] {
  const v = sheet.values;
  // Check for proficiencies stored as comma-separated string or array
  if (Array.isArray(v.proficiencies)) {
    return (v.proficiencies as string[]).filter(p => typeof p === 'string' && p.length > 0);
  }
  if (typeof v.proficiencies === 'string' && v.proficiencies.length > 0) {
    return v.proficiencies.split(',').map(s => s.trim()).filter(Boolean);
  }
  // Also check for individual proficiency fields
  const result: string[] = [];
  if (typeof v.armor_proficiencies === 'string' && v.armor_proficiencies) {
    result.push(...v.armor_proficiencies.split(',').map(s => s.trim()).filter(Boolean));
  }
  if (typeof v.weapon_proficiencies === 'string' && v.weapon_proficiencies) {
    result.push(...v.weapon_proficiencies.split(',').map(s => s.trim()).filter(Boolean));
  }
  if (typeof v.tool_proficiencies === 'string' && v.tool_proficiencies) {
    result.push(...v.tool_proficiencies.split(',').map(s => s.trim()).filter(Boolean));
  }
  if (typeof v.languages === 'string' && v.languages) {
    result.push(...v.languages.split(',').map(s => s.trim()).filter(Boolean));
  }
  return result;
}

function mapResources(sheet: CharacterSheet): RulesCharacterData['resources'] {
  // Resources live on sheet.values.resources — that's where the dedicated
  // sheet:resource-add/-edit/-remove handlers write them (flat objects). Reading
  // repeatingData.resources here was a read/write asymmetry: nothing ever writes
  // there, so the HUD "Ресурсы" panel stayed permanently empty (BF 2026-06-09).
  const raw = (sheet.values as Record<string, unknown> | undefined)?.resources;
  const list = Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : [];
  return list.map((r, i) => ({
    id: typeof r.id === 'string' ? r.id : `resource-${i}`,
    name: coerceString(r.name, 'Ресурс'),
    current: coerceNumber(r.current, 0),
    max: coerceNumber(r.max, 0),
    recharge: coerceString(r.recharge, 'long'),
  }));
}

/**
 * Parse the persisted roleplay blob. The granular `sheet:rp-*` handlers
 * persist into `sheet.values.roleplay_json` as a plain object
 * (server/src/commands/sheet-rp.ts: getRoleplayJson). Over the wire the
 * blob arrives already-parsed, but we also tolerate a JSON string form
 * (mirrors server/src/ai/llm/context-builder.ts which handles both).
 * Returns undefined for absent/null/malformed/non-object payloads so the
 * caller falls back to an empty roleplay shape.
 */
function parseRoleplayJson(raw: unknown): Record<string, unknown> | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string') {
    if (raw.length === 0) return undefined;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return undefined;
}

function parseBoardConfig(raw: unknown): RulesCharacterData['rp']['boardConfig'] {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    return {
      cards: Array.isArray(obj.cards) ? (obj.cards as unknown[]) : [],
      autoLayout: obj.autoLayout === true,
    };
  }
  return { cards: [], autoLayout: false };
}

function asList<T>(raw: unknown): T[] {
  return Array.isArray(raw) ? (raw as T[]) : [];
}

// Display-span defaults per card type (mirror DEFAULT_BOARD_CONFIG in the
// client store) used when auto-seeding a missing content card.
const RP_CARD_SPAN_DEFAULTS: Record<string, number> = {
  hero: 5, triada: 4, manners: 3, backstory: 7, goals: 5,
  npc: 6, factions: 6, sessionLog: 7, moments: 5,
};

/** Ensure every roleplay field that HAS content owns a card on the board.
 *
 * The RP board renders exactly the cards in `boardConfig.cards`. Real sheets
 * ship with an empty/sparse persisted board (emptyRoleplay → cards:[]), and
 * persona authored via the flat sheet fields or update_character_sheet lands
 * with no card at all — so authored content was silently invisible (a full
 * backstory with no backstory card renders nothing). Preserve the existing
 * cards + their order/layout; append a default card for any content-bearing
 * field that lacks one so nothing is unreachable. */
function ensureContentCards(
  cards: unknown[],
  rp: Pick<RulesCharacterData['rp'],
    'ideal' | 'bond' | 'flaw' | 'backstory' | 'manners' |
    'goals' | 'npcs' | 'factions' | 'sessionLog' | 'moments'>,
): unknown[] {
  const present = new Set(
    cards
      .map((c) => (c && typeof c === 'object' ? (c as { type?: unknown }).type : undefined))
      .filter((t): t is string => typeof t === 'string'),
  );
  const needed: string[] = [];
  if (rp.ideal || rp.bond || rp.flaw) needed.push('triada');
  if (rp.backstory) needed.push('backstory');
  if (rp.manners) needed.push('manners');
  if (rp.goals.length) needed.push('goals');
  if (rp.npcs.length) needed.push('npc');
  if (rp.factions.length) needed.push('factions');
  if (rp.sessionLog.length) needed.push('sessionLog');
  if (rp.moments.length) needed.push('moments');

  const out = [...cards];
  let order = out.length;
  for (const type of needed) {
    if (present.has(type)) continue;
    out.push({
      id: `auto-${type}`,
      type,
      span: RP_CARD_SPAN_DEFAULTS[type] ?? 4,
      style: 'plain',
      pinned: false,
      order: order++,
    });
  }
  return out;
}

function mapRoleplay(sheet: CharacterSheet): RulesCharacterData['rp'] {
  // T002 (2026-06-09): read/write asymmetry fix. The granular sheet:rp-*
  // handlers persist persona fields + lists + boardConfig into the nested
  // `values.roleplay_json` blob (server/src/commands/sheet-rp.ts). Read them
  // back so RP survives a round-trip.
  const v = (sheet.values as Record<string, unknown> | undefined) ?? {};
  const rp = parseRoleplayJson(v.roleplay_json) ?? {};

  // Flat-field fallback (2026-07-05): persona authored via the classic sheet
  // fields OR update_character_sheet lands in the flat template values
  // (values.ideals/bonds/flaws/personality_traits), but the RP board reads
  // roleplay_json. Bridge them (roleplay_json wins, flat is fallback) so the
  // Triada/Manners cards are not empty when the content lives flat.
  // roleplay_json is authoritative once a field exists there: bridge from the
  // flat field ONLY when the roleplay_json key is ABSENT (undefined), not when
  // it's an empty string — otherwise clearing a persona field in the RP board
  // can't stick, because `|| flat` keeps resurrecting the stale flat value.
  const bridge = (rpKey: string, flatKey: string): string =>
    rp[rpKey] !== undefined ? coerceString(rp[rpKey]) : coerceString(v[flatKey]);
  const ideal = bridge('ideal', 'ideals');
  const bond = bridge('bond', 'bonds');
  const flaw = bridge('flaw', 'flaws');
  const backstory = bridge('backstory', 'backstory');
  const manners = bridge('manners', 'personality_traits');
  const content = {
    ideal, bond, flaw, backstory, manners,
    goals: asList<RulesCharacterData['rp']['goals'][number]>(rp.goals),
    npcs: asList<RulesCharacterData['rp']['npcs'][number]>(rp.npcs),
    factions: asList<RulesCharacterData['rp']['factions'][number]>(rp.factions),
    sessionLog: asList<RulesCharacterData['rp']['sessionLog'][number]>(rp.sessionLog),
    moments: asList<RulesCharacterData['rp']['moments'][number]>(rp.moments),
  };
  const board = parseBoardConfig(rp.boardConfig);
  // Auto-seed content cards ONLY for a pristine (empty) board — the first time a
  // sheet is opened, so authored persona isn't invisible. Once the board holds
  // cards it is user-curated: respect it verbatim, or a card the user DELETED
  // would be resurrected on the next read (its content still lives in
  // roleplay_json). Edge: deleting ALL cards re-seeds (empty is indistinguishable
  // from never-initialised without a separate flag) — acceptable vs the common
  // delete-one-of-many case this protects.
  const cards = board.cards.length === 0 ? ensureContentCards(board.cards, content) : board.cards;
  return {
    ...content,
    boardConfig: { cards, autoLayout: board.autoLayout },
  };
}

/** Lookup hit die size for a class slug */
function getHitDieForClass(classSlug: string): number {
  const hitDieMap: Record<string, number> = {
    barbarian: 12, fighter: 10, paladin: 10, ranger: 10,
    bard: 8, cleric: 8, druid: 8, monk: 8, rogue: 8, warlock: 8,
    sorcerer: 6, wizard: 6,
  };
  return hitDieMap[classSlug] ?? 8;
}
